import { createRouter, type ParsedLocation } from '@tanstack/react-router'

import { queryClient } from '@/app/query-client'
import PendingPage from '@/components/layout/PendingPage'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import { PAGE_PENDING_MIN_MS, PAGE_PENDING_MS } from '@/lib/motion/page-frame'
import { routeTree } from '@/routeTree.gen'

/** The pathname the last completed render landed on. See `shouldRestoreScroll`. */
let renderedPathname: string | null = null

/**
 * Whether the router is allowed to move the scroll position for this render.
 *
 * Screens in this app keep their state in the URL: Floorsheet's grouping tabs,
 * Movers' period chips, every filter select and every pager write a search
 * param and let the route re-read it. The router cannot tell those apart from
 * navigation. It keys its scroll cache by history entry, so a search-param
 * change is a fresh entry with nothing saved against it, and it falls through
 * to sending the window to 0 — a member reading row 40 of the floorsheet and
 * switching to By symbol is thrown back to the top, which reads as a reload.
 *
 * So the scroll position is left alone whenever a render stays on the pathname
 * it was already on, and handled normally otherwise. Real navigation still
 * lands at the top and back/forward still restores, because both change the
 * pathname. Note this leaves scroll untouched rather than saving and restoring
 * it, so it does not depend on the cache having a usable entry.
 *
 * A router-level rule rather than `resetScroll: false` on each of the
 * twenty-six call sites that write a search param: it is one statement about
 * what a search param means here, and the next screen inherits it for free.
 * The one thing it gives up is same-page `#anchor` links, which would need the
 * router to scroll while the pathname holds. Nothing in the app uses one.
 */
const shouldRestoreScroll = ({ location }: { location: ParsedLocation }) => {
  const staysOnPage = renderedPathname === location.pathname
  renderedPathname = location.pathname
  return !staysOnPage
}

/*
 * ── Search params, and why no loader declares `loaderDeps` ──────────────────
 *
 * A route's loader awaits the page's MAIN data, and the router shows the
 * route's pending skeleton while it does. `loaderDeps` would make each search
 * value part of the route MATCH's identity, so a filter, tab, pager or period
 * change would start a new match and put the whole page back on its skeleton
 * — exactly what a member clicking a chip must not see.
 *
 * So a loader runs when its route is entered (or its path params change),
 * and never for a search-only change: with no deps the match stays the same
 * one, and the router does not re-run the loader of a match that stays. When
 * a loader needs the URL's search to pick its first read, it parses
 * `location.search` through the route's own `validateSearch` schema — the
 * same parse the router applies, so the two cannot disagree. After that, the
 * screen owns search-driven data: `useQuery` with `keepPreviousData`, the old
 * slice up (`RefreshRegion`, `UpdatingNote`) until the new one lands. Nor
 * does the page's entrance replay: that plays on mount (globals.css's PAGE
 * ENTER), and a search-only change keeps the page mounted.
 */
export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  /**
   * React Query is the only cache; the router keeps no second copy of any
   * route's data. With a preload stale time of 0 every hover re-runs the
   * route's loader — which costs nothing, because the loader's
   * `ensureRouteData` hands back whatever the cache already holds without a
   * request, and its `prefetchRouteData` only fetches what has gone stale. So
   * a hover never refetches data that is fresh, and the router never serves
   * a loader result React Query has since replaced.
   */
  defaultPreloadStaleTime: 0,
  /**
   * The router's default ('background') re-runs a stale loader AFTER
   * rendering the match it already has — typically one left by a hover
   * preload. That match carries no data of ours (loaders return nothing; the
   * data is in React Query), so the screen would mount before its main data
   * is known to be cached, on a search the preload may not even have used.
   * Blocking costs a microtask when the data is cached, which it almost
   * always is by then, and guarantees the screen's first render has it.
   */
  defaultStaleReloadMode: 'blocking',
  // The destination's pending page shows within a frame of the click — its
  // title row and rail enter at once, and its skeleton waits 80ms before it
  // paints. See page-frame.ts.
  defaultPendingMs: PAGE_PENDING_MS,
  defaultPendingMinMs: PAGE_PENDING_MIN_MS,
  defaultPendingComponent: PendingPage,
  defaultErrorComponent: RouteErrorPage,
  /**
   * A page's entrance plays when its component mounts, and without this a
   * path-param change on a cached route — /stocks/NABIL to /stocks/UPPER —
   * re-renders the mounted screen instead: a new page with no entrance, and
   * with the last stock's local state (an open panel, a picked tab) carried
   * over to the next. An uncached one already remounted, through its pending
   * page; this makes both do it. Keyed on params only, so a search-param
   * change still keeps the page. A route whose screen must outlive one of
   * its params narrows this with its own `remountDeps` (the lecture player
   * keys on its course, not its lecture).
   */
  defaultRemountDeps: ({ params }) => params,
  scrollRestoration: shouldRestoreScroll,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
